import * as React from 'react';
import { clamp, cn } from '../../lib/utils';

export interface ConfidenceBarProps extends React.HTMLAttributes<HTMLDivElement> {
  /** 0..1 score from the AI verdict. */
  score: number;
  /** Show numeric label inline. */
  showLabel?: boolean;
  /** Threshold above which the bar turns to "high" colour. */
  highThreshold?: number;
  /** Threshold below which the bar turns to "low" colour. */
  lowThreshold?: number;
  ariaLabel?: string;
}

/**
 * AI verdict confidence bar — instrument meter.
 *
 * Discrete colour bands match operator training:
 *   ≥0.85 → ai-high (emerald)
 *   0.6–0.85 → ai-mid (amber)
 *   <0.6 → ai-low (red)
 *
 * Square-cornered track + 1px hairline framing for the instrument feel,
 * NOT a rounded SaaS progress pill. Numeric label uses tabular nums so the
 * column doesn't jitter as values update.
 */
function ConfidenceBar({
  score,
  showLabel = true,
  highThreshold = 0.85,
  lowThreshold = 0.6,
  ariaLabel = 'AI confidence',
  className,
  ...props
}: ConfidenceBarProps): React.JSX.Element {
  const pct = clamp(score, 0, 1);
  const tier = pct >= highThreshold ? 'high' : pct >= lowThreshold ? 'mid' : 'low';
  const color = {
    high: 'bg-[var(--color-ai-high)]',
    mid: 'bg-[var(--color-ai-mid)]',
    low: 'bg-[var(--color-ai-low)]',
  }[tier];

  return (
    <div
      role="progressbar"
      aria-label={ariaLabel}
      aria-valuemin={0}
      aria-valuemax={100}
      aria-valuenow={Math.round(pct * 100)}
      className={cn('flex items-center gap-2', className)}
      {...props}
    >
      <div className="relative h-1.5 flex-1 overflow-hidden border border-[var(--color-hairline)] bg-[var(--color-surface-2)]">
        <div
          className={cn('h-full transition-[width] duration-300', color)}
          style={{ width: `${pct * 100}%` }}
        />
      </div>
      {showLabel && (
        <span className="font-mono text-[0.6875rem] tabular-nums text-[var(--color-foreground-3)] min-w-[2.5ch] text-end">
          {(pct * 100).toFixed(0)}%
        </span>
      )}
    </div>
  );
}

export { ConfidenceBar };
